import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import type { Session } from '../../api/session';
import { getSystemMessages, markSystemMessageRead } from '../../api/tenant';
import { moscowDayLabel, moscowTime, parseServerDate } from '../../domain/moscowTime';
import s from './home.module.css';

/** One card: on a small phone more would push «Заказать пропуск» below the fold. */
const SHOWN = 1;

/**
 * The newest unread announcement of the managing company on top of the home screen (roadmap P2-13): until now they hid
 * behind the bell. «×» marks one as read; the rest are read on the notifications screen.
 */
export function Announcements({ session, unread, compact = false }: { session: Session; unread: number; compact?: boolean }) {
  const queryClient = useQueryClient();
  const [hidden, setHidden] = useState<number[]>([]);
  const messages = useQuery({ queryKey: ['system-messages'], queryFn: () => getSystemMessages(session), enabled: unread > 0 });

  const fresh = (messages.data ?? [])
    .filter((m) => m.new && !hidden.includes(m.id))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  if (!fresh.length) return null;

  const dismiss = (id: number) => {
    setHidden((list) => [...list, id]);
    void markSystemMessageRead(session, id)
      .catch(() => undefined)
      .then(() => queryClient.invalidateQueries({ queryKey: ['new-count'] }));
  };
  const now = new Date();
  const rest = fresh.length - SHOWN;

  if (compact) {
    // Home screen B: one line, so the partner banner still fits the first screen of a 600 px window.
    const m = fresh[0];
    return (
      <section className={s.teaser} aria-label="Объявления управляющей компании">
        <Link to="/notifications" className={s.teaserBody}>
          <span className={s.teaserDot} aria-hidden="true" />
          <span className={s.teaserText}>
            <b>{m.title}</b> {m.text}
          </span>
          {rest > 0 && <span className={s.teaserMore}>ещё {rest}</span>}
        </Link>
        <button type="button" className={s.announcementClose} aria-label={`Прочитано: ${m.title}`} onClick={() => dismiss(m.id)}>
          ×
        </button>
      </section>
    );
  }

  return (
    <section className={s.announcements} aria-label="Объявления управляющей компании">
      {fresh.slice(0, SHOWN).map((m) => {
        const at = parseServerDate(m.createdAt.replace('T', ' ').slice(0, 19));
        return (
          <article key={m.id} className={s.announcement}>
            <Link to="/notifications" className={s.announcementBody}>
              <span className={s.announcementTitle}>{m.title}</span>
              <span className={s.announcementText}>{m.text}</span>
              <span className={s.announcementSub}>
                {m.systemName}
                {at && !Number.isNaN(at.getTime()) ? ` · ${moscowDayLabel(at, now)} ${moscowTime(at)}` : ''}
              </span>
            </Link>
            <button type="button" className={s.announcementClose} aria-label={`Прочитано: ${m.title}`} onClick={() => dismiss(m.id)}>
              ×
            </button>
          </article>
        );
      })}
      {rest > 0 && (
        <Link to="/notifications" className={s.tertiary}>
          Ещё {rest} {rest === 1 ? 'объявление' : rest < 5 ? 'объявления' : 'объявлений'} →
        </Link>
      )}
    </section>
  );
}
